import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { ArrowLeft, DollarSign, Zap, TrendingUp, Clock, Leaf, Download, MapPin, AlertTriangle, CreditCard, Satellite, Database, Settings } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Project } from '../types';
import { getProject } from '../utils/api';
import { formatCurrency, formatDate, formatEnergy, formatPercent, formatCoordinates, formatNumber, getMonthName } from '../utils/formatters';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { calculateFinancialMetrics } from '../utils/calculations';

export function ProjectDetails() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadProject() {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching project with ID:', id);
        const p = await getProject(id);
        console.log('Project fetched:', p);
        
        // CRITICAL FIX: Recalculate LCOE if it's 0 or invalid (legacy projects)
        if (p.results && (p.results.financialMetrics.lcoe === 0 || !p.results.financialMetrics.lcoe)) {
          console.warn('[ProjectDetails] LCOE is 0 or invalid, recalculating...');
          const correctedMetrics = calculateFinancialMetrics(
            p.results.energyProduction,
            p.financialParams,
            p.systemConfig.capacity
          );
          
          // Update project with corrected metrics
          p.results.financialMetrics = correctedMetrics;
          console.log('[ProjectDetails] Corrected LCOE:', correctedMetrics.lcoe);
        }
        
        setProject(p);
      } catch (err) {
        console.error('Failed to load project:', err);
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    }
    
    loadProject();
  }, [id]);

  if (loading) {
    return <LoadingSpinner message={t('projectDetails.loading', 'Loading Project...')} />;
  }

  if (error || !project) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-600">{error || t('projectDetails.projectNotFound')}</p>
        <Link to="/" className="text-amber-600 hover:underline mt-4 inline-block">
          {t('projectDetails.backToDashboard')}
        </Link>
      </div>
    );
  }

  if (!project.results) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-600">{t('projectDetails.noResults')}</p>
        <Link to="/" className="text-amber-600 hover:underline mt-4 inline-block">
          {t('projectDetails.backToDashboard')}
        </Link>
      </div>
    );
  }

  const { results, systemConfig, financialParams, location } = project;
  const { energyProduction, financialMetrics, solarData, environmentalImpact } = results;

  // Prepare chart data
  const monthlyProductionData = energyProduction.monthlyProduction.map((value, index) => {
    const ghiValue = solarData.monthlyGHI && solarData.monthlyGHI[index] 
      ? parseFloat(solarData.monthlyGHI[index].toFixed(2))
      : 0;
    
    return {
      month: getMonthName(index),
      production: Math.round(value),
      ghi: ghiValue,
    };
  });

  const cashFlowData = financialMetrics.cashFlow.map((cf) => ({
    year: cf.year,
    cumulative: Math.round(cf.cumulativeCashFlow),
    netCashFlow: Math.round(cf.netCashFlow),
  }));

  const totalCapex = Object.values(financialParams.capex).reduce((sum, val) => sum + (val || 0), 0);

  const costBreakdown = [
    { name: 'Panels', value: financialParams.capex.panelCost },
    { name: 'Inverter', value: financialParams.capex.inverterCost },
    { name: 'BOS', value: financialParams.capex.bosCost },
    { name: 'Soft Costs', value: financialParams.capex.softCosts },
  ];

  const COLORS = ['#f59e0b', '#fb923c', '#fdba74', '#fed7aa'];

  const handleExportPDF = async () => {
    if (!project || !project.results) return;
    
    setIsGeneratingPDF(true);
    toast.loading('Generating PDF report...', { id: 'pdf-generation' });
    
    try {
      const { generatePDFReport } = await import('../utils/pdfExport');
      
      await generatePDFReport(project, undefined, undefined, undefined, undefined, {
        includeExecutiveSummary: true,
        includeDetailedAnalysis: true,
        includeRiskAnalysis: false,
        includeFinancingOptions: false,
      });
      
      toast.success('PDF report generated successfully!', { id: 'pdf-generation' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.', { id: 'pdf-generation' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('projectDetails.backToDashboard')}
        </Link>
        
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-slate-900 mb-3">{project.name}</h1>
            <div className="flex items-center gap-4 text-slate-600">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{formatCoordinates(location.latitude, location.longitude)}</span>
              </div>
              {location.country && (
                <span className="px-3 py-1 bg-slate-100 rounded-full text-sm font-medium">
                  {location.country}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative group">
              <button
                disabled={true}
                className="px-6 py-2.5 bg-slate-300 text-slate-500 rounded-lg cursor-not-allowed flex items-center gap-2 shadow-sm opacity-60"
              >
                <AlertTriangle className="w-4 h-4" />
                {t('projectDetails.riskAnalysis')}
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg">
                <div className="text-center font-medium">Coming Soon</div>
                <div className="text-slate-300 mt-0.5">This feature will be available shortly</div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="border-4 border-transparent border-t-slate-900"></div>
                </div>
              </div>
            </div>
            
            <div className="relative group">
              <button
                disabled={true}
                className="px-6 py-2.5 bg-slate-300 text-slate-500 rounded-lg cursor-not-allowed flex items-center gap-2 shadow-sm opacity-60"
              >
                <CreditCard className="w-4 h-4" />
                {t('projectDetails.financing')}
              </button>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap shadow-lg">
                <div className="text-center font-medium">Coming Soon</div>
                <div className="text-slate-300 mt-0.5">This feature will be available shortly</div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="border-4 border-transparent border-t-slate-900"></div>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleExportPDF}
              disabled={isGeneratingPDF}
              className="px-6 py-2.5 bg-[#4F819F] text-white rounded-lg hover:bg-[#3d6580] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isGeneratingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {t('projectDetails.generating')}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  {t('projectDetails.exportPDF')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title={t('projectDetails.npv')}
          value={formatCurrency(financialMetrics.npv, financialParams.currency)}
          subtitle={financialMetrics.npv > 0 ? t('projectDetails.positiveROI') : t('projectDetails.negativeROI')}
          icon={DollarSign}
          variant={financialMetrics.npv > 0 ? 'success' : 'warning'}
        />
        <MetricCard
          title={t('projectDetails.paybackPeriod')}
          value={`${financialMetrics.paybackPeriod.toFixed(1)} ${t('projectDetails.yrs')}`}
          subtitle={`${t('projectDetails.irr')}: ${formatPercent(financialMetrics.irr)}`}
          icon={Clock}
          variant="neutral"
        />
        <MetricCard
          title={t('projectDetails.annualProduction')}
          value={formatEnergy(energyProduction.annualProduction)}
          subtitle={`${formatNumber(energyProduction.specificYield, 0)} ${t('projectDetails.kWhPerKWp')}`}
          icon={Zap}
          variant="warning"
        />
        <MetricCard
          title={t('projectDetails.totalROI')}
          value={formatPercent(financialMetrics.roi)}
          subtitle={`${t('projectDetails.over')} ${financialParams.projectLifetime} ${t('projectDetails.years')}`}
          icon={TrendingUp}
          variant={financialMetrics.roi > 0 ? 'success' : 'warning'}
        />
      </div>

      {/* System Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{t('projectDetails.systemConfig')}</h2>
            <p className="text-sm text-slate-600">Technical specifications and system parameters</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <h3 className="font-semibold text-slate-700 mb-3">{t('projectDetails.systemSpecs')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.capacity')}:</span>
                <span className="font-medium">{systemConfig.capacity} kWp</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.panelType')}:</span>
                <span className="font-medium">{systemConfig.panelType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.systemType')}:</span>
                <span className="font-medium">{systemConfig.systemType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.inverterEfficiency')}:</span>
                <span className="font-medium">{systemConfig.inverterEfficiency}%</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-700 mb-3">{t('projectDetails.systemLosses')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.soiling')}:</span>
                <span className="font-medium">{systemConfig.losses.soiling}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.shading')}:</span>
                <span className="font-medium">{systemConfig.losses.shading}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.dcLoss')}:</span>
                <span className="font-medium">{systemConfig.losses.dcLoss}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.acLoss')}:</span>
                <span className="font-medium">{systemConfig.losses.acLoss}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.degradation')}:</span>
                <span className="font-medium">{systemConfig.losses.degradation}%/{t('projectDetails.year')}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-700 mb-3">{t('projectDetails.solarData')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.dataSource')}:</span>
                <span className="font-medium">NASA POWER</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.avgTemperature')}:</span>
                <span className="font-medium">{formatNumber(solarData.avgTemperature, 1)}°C</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.latitude')}:</span>
                <span className="font-medium">{location.latitude.toFixed(4)}°</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">{t('projectDetails.longitude')}:</span>
                <span className="font-medium">{location.longitude.toFixed(4)}°</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Sources & Methodology */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <Satellite className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Data Sources & Methodology</h2>
            <p className="text-sm text-slate-600">Real satellite data & industry-standard calculations</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* NASA POWER Data */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Satellite className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-slate-900">NASA POWER API</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Data Sources</p>
                <p className="text-sm font-bold text-slate-900">CERES & MERRA2 Satellites</p>
              </div>
              
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Data Period</p>
                <p className="text-sm font-bold text-slate-900">2020-2023 (4-year averages)</p>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Spatial Resolution</p>
                <p className="text-sm font-bold text-slate-900">~0.5° × 0.625° (~50 km)</p>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Parameters Retrieved</p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Monthly GHI:</span>
                    <span className="font-medium text-amber-600">{formatNumber(solarData.monthlyGHI.reduce((a, b) => Math.max(a, b), 0), 1)} kWh/m²/day (peak)</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Annual GHI:</span>
                    <span className="font-medium text-amber-600">{formatNumber(solarData.annualGHI, 0)} kWh/m²/year</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Avg. Temperature:</span>
                    <span className="font-medium text-slate-900">{formatNumber(solarData.avgTemperature, 1)}°C</span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-blue-100">
                <p className="text-xs text-blue-700">
                  ✓ Validated against ground stations<br/>
                  ✓ Used by PVGIS, SAM, RETScreen<br/>
                  ✓ Updated daily from satellite feeds
                </p>
              </div>
            </div>
          </div>

          {/* Calculation Methodology */}
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-slate-900">Calculation Methodology</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Energy Production</p>
                <div className="text-xs text-slate-600 space-y-1 bg-slate-50 rounded p-2">
                  <p><strong>Performance Ratio:</strong> {formatPercent(energyProduction.performanceRatio)}</p>
                  <p className="text-[11px] leading-tight">
                    = (100% - Soiling - Shading - DC Loss - AC Loss) × Inverter Efficiency
                  </p>
                  <p className="text-[11px] leading-tight mt-1">
                    Monthly Production = GHI × Days × Capacity × PR
                  </p>
                  <p className="text-[11px] leading-tight">
                    Degradation: {systemConfig.losses.degradation}%/year over 25 years
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Financial Analysis</p>
                <div className="text-xs text-slate-600 space-y-1 bg-slate-50 rounded p-2">
                  <p><strong>NPV:</strong> {formatCurrency(financialMetrics.npv, financialParams.currency)}</p>
                  <p className="text-[11px] leading-tight">
                    Net Present Value with {financialParams.discountRate}% discount rate
                  </p>
                  <p className="text-[11px] leading-tight mt-1">
                    <strong>IRR:</strong> {formatPercent(financialMetrics.irr)} (Newton-Raphson method)
                  </p>
                  <p className="text-[11px] leading-tight mt-1">
                    <strong>LCOE:</strong> ${formatNumber(financialMetrics.lcoe, 3)}/kWh
                  </p>
                  <p className="text-[11px] leading-tight">
                    Levelized Cost of Energy (lifetime avg.)
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Environmental Impact</p>
                <div className="text-xs text-slate-600 space-y-1 bg-slate-50 rounded p-2">
                  <p className="text-[11px] leading-tight">
                    <strong>CO Factor:</strong> 0.475 kg CO₂/kWh
                  </p>
                  <p className="text-[11px] leading-tight">
                    (Global electricity grid average)
                  </p>
                  <p className="text-[11px] leading-tight mt-1">
                    <strong>Trees:</strong> 21 kg CO₂/tree/year × 25 years
                  </p>
                  <p className="text-[11px] leading-tight mt-1">
                    <strong>Coal:</strong> 2.86 tons CO₂ per ton burned
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly GHI Data Table */}
        <div className="mt-6 bg-slate-50 rounded-lg border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
            <span>Monthly Solar Radiation Data (NASA POWER)</span>
            <span className="text-xs font-normal text-slate-500">2020-2023 averages</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-semibold text-slate-700">Month</th>
                  {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
                    <th key={m} className="text-center py-2 px-2 font-semibold text-slate-700">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-2 px-3 font-medium text-slate-600">GHI (kWh/m²/day)</td>
                  {solarData.monthlyGHI.map((ghi, i) => {
                    const maxGhi = Math.max(...solarData.monthlyGHI);
                    const isMax = ghi === maxGhi;
                    return (
                      <td key={i} className={`text-center py-2 px-2 font-mono ${isMax ? 'bg-amber-100 font-bold text-amber-900' : 'text-slate-900'}`}>
                        {ghi.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="py-2 px-3 font-medium text-slate-600">Production (kWh)</td>
                  {energyProduction.monthlyProduction.map((prod, i) => {
                    const maxProd = Math.max(...energyProduction.monthlyProduction);
                    const isMax = prod === maxProd;
                    return (
                      <td key={i} className={`text-center py-2 px-2 font-mono text-xs ${isMax ? 'bg-amber-100 font-bold text-amber-900' : 'text-slate-900'}`}>
                        {formatNumber(prod, 0)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            <strong>Note:</strong> GHI (Global Horizontal Irradiance) represents the total solar radiation received on a horizontal surface. 
            Production values include system losses, Performance Ratio ({formatPercent(energyProduction.performanceRatio)}), and degradation.
          </p>
        </div>
      </div>

      {/* Energy Production */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{t('projectDetails.energyProductionAnalysis')}</h2>
            <p className="text-sm text-slate-600">Monthly generation forecast and performance metrics</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="text-sm text-amber-800 mb-1">{t('projectDetails.annualGHI')}</p>
            <p className="text-2xl font-bold text-amber-900">
              {formatNumber(solarData.annualGHI, 0)} kWh/m²
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-800 mb-1">{t('projectDetails.performanceRatio')}</p>
            <p className="text-2xl font-bold text-slate-900">{formatPercent(energyProduction.performanceRatio)}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
            <p className="text-sm text-amber-800 mb-1">{t('projectDetails.optimalTiltAngle')}</p>
            <p className="text-2xl font-bold text-amber-900">{formatNumber(solarData.optimalTiltAngle, 0)}°</p>
          </div>
        </div>

        <h3 className="font-semibold text-slate-900 mb-4">{t('projectDetails.monthlyProductionSolarRadiation')}</h3>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={monthlyProductionData}>
            <CartesianGrid key="production-grid" strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis key="production-xaxis" dataKey="month" stroke="#64748b" />
            <YAxis 
              key="left-axis"
              yAxisId="left" 
              stroke="#64748b" 
              label={{ value: `${t('projectDetails.production')} (kWh)`, angle: -90, position: 'insideLeft' }} 
            />
            <YAxis 
              key="right-axis"
              yAxisId="right" 
              orientation="right" 
              stroke="#64748b" 
              label={{ value: 'GHI (kWh/m²/day)', angle: 90, position: 'insideRight' }} 
            />
            <Tooltip
              key="production-tooltip"
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
            />
            <Legend key="production-legend" />
            <Bar 
              key="production-bar"
              yAxisId="left" 
              dataKey="production" 
              fill="#f59e0b" 
              name={`${t('projectDetails.monthlyProduction')} (kWh)`}
              isAnimationActive={false}
            />
            <Line 
              key="ghi-line"
              yAxisId="right" 
              type="monotone" 
              dataKey="ghi" 
              stroke="#ef4444" 
              strokeWidth={2} 
              name="GHI (kWh/m²/day)" 
              dot={{ fill: '#ef4444', r: 4 }} 
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Monthly Production Calculation Table */}
        <div className="mt-8 bg-slate-50 rounded-lg border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
            <span>Monthly Production Calculation Breakdown</span>
            <span className="text-xs font-normal text-slate-500 bg-amber-100 px-2 py-1 rounded">Year 1 (no degradation)</span>
          </h3>
          <p className="text-xs text-slate-600 mb-4">
            Formula: <strong>Production = GHI × Days × System Capacity × Performance Ratio × Degradation Factor</strong>
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b-2 border-slate-300 bg-slate-100">
                  <th className="text-left py-2 px-3 font-bold text-slate-700">Month</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">GHI<br/>(kWh/m²/day)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Days</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Capacity<br/>(kWp)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">PR<br/>(%)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">DC Output<br/>(kWh)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">System Losses<br/>(%)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700 bg-amber-50">AC Output<br/>(kWh)</th>
                </tr>
              </thead>
              <tbody>
                {solarData.monthlyGHI.map((ghi, index) => {
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
                  const days = daysInMonth[index];
                  const capacity = systemConfig.capacity;
                  const pr = energyProduction.performanceRatio / 100; // Convert to decimal
                  
                  // DC Output = GHI × Days × Capacity (theoretical max)
                  const dcOutput = ghi * days * capacity;
                  
                  // Total system losses
                  const totalLosses = (1 - pr) * 100;
                  
                  // AC Output = DC Output × PR (actual production from backend)
                  const acOutput = energyProduction.monthlyProduction[index];
                  
                  const isHighProduction = acOutput === Math.max(...energyProduction.monthlyProduction);
                  
                  return (
                    <tr key={index} className={`border-b border-slate-100 hover:bg-slate-50 ${isHighProduction ? 'bg-amber-50' : ''}`}>
                      <td className="py-2 px-3 font-medium text-slate-700">{monthNames[index]}</td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">{ghi.toFixed(2)}</td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">{days}</td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">{capacity}</td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">{(pr * 100).toFixed(1)}%</td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">{formatNumber(dcOutput, 0)}</td>
                      <td className="text-right py-2 px-3 font-mono text-red-600">{totalLosses.toFixed(1)}%</td>
                      <td className="text-right py-2 px-3 font-mono font-bold text-amber-900 bg-amber-50">{formatNumber(acOutput, 0)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                  <td className="py-3 px-3 text-slate-900">ANNUAL TOTAL</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">{formatNumber(solarData.annualGHI / 365, 2)}</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">365</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">{systemConfig.capacity}</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">{formatPercent(energyProduction.performanceRatio)}</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">{formatNumber((solarData.annualGHI / 365) * 365 * systemConfig.capacity, 0)}</td>
                  <td className="text-right py-3 px-3 font-mono text-red-600">{((1 - energyProduction.performanceRatio / 100) * 100).toFixed(1)}%</td>
                  <td className="text-right py-3 px-3 font-mono font-bold text-amber-900 bg-amber-100 text-lg">{formatNumber(energyProduction.annualProduction, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Performance Ratio Breakdown:</p>
              <p className="text-slate-600">• Inverter Efficiency: {systemConfig.inverterEfficiency}%</p>
              <p className="text-slate-600">• Soiling Loss: {systemConfig.losses.soiling}%</p>
              <p className="text-slate-600">• Shading Loss: {systemConfig.losses.shading}%</p>
              <p className="text-slate-600">• DC/AC Losses: {systemConfig.losses.dcLoss + systemConfig.losses.acLoss}%</p>
              <p className="font-semibold text-amber-700 mt-1">= Final PR: {formatPercent(energyProduction.performanceRatio)}</p>
            </div>
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Annual Performance:</p>
              <p className="text-slate-600">• Specific Yield: {formatNumber(energyProduction.specificYield, 0)} kWh/kWp</p>
              <p className="text-slate-600">• Capacity Factor: {formatPercent(energyProduction.annualProduction / (systemConfig.capacity * 8760))}</p>
              <p className="text-slate-600">• Peak Month: {getMonthName(energyProduction.monthlyProduction.indexOf(Math.max(...energyProduction.monthlyProduction)))}</p>
            </div>
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Data Quality:</p>
              <p className="text-slate-600">✓ NASA POWER API (satellite-validated)</p>
              <p className="text-slate-600">✓ 4-year average (2020-2023)</p>
              <p className="text-slate-600">✓ ~50km spatial resolution</p>
              <p className="text-slate-600">✓ Industry-standard methodology</p>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{t('projectDetails.financialAnalysis')}</h2>
            <p className="text-sm text-slate-600">Investment analysis & economic performance</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-700">{t('projectDetails.investment')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('projectDetails.totalCAPEX')}:</span>
                <span className="font-medium text-slate-900">{formatCurrency(totalCapex, financialParams.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('projectDetails.annualOPEX')}:</span>
                <span className="font-medium text-slate-900">
                  {formatCurrency(
                    Object.values(financialParams.opex).reduce((sum, val) => sum + val, 0),
                    financialParams.currency
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-700">{t('projectDetails.returns')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('projectDetails.totalRevenue')}:</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(financialMetrics.totalRevenue, financialParams.currency)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('projectDetails.netProfit')}:</span>
                <span className="font-medium text-green-600">
                  {formatCurrency(financialMetrics.netProfit, financialParams.currency)}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-700">{t('projectDetails.metrics')}</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('projectDetails.lcoe')}:</span>
                <span className="font-medium text-slate-900">
                  ${formatNumber(financialMetrics.lcoe, 3)}/kWh
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('projectDetails.irr')}:</span>
                <span className="font-medium text-slate-900">{formatPercent(financialMetrics.irr)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Breakdown Pie Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="font-semibold text-slate-900 mb-4">{t('projectDetails.capexBreakdown')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={costBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {costBreakdown.map((entry, index) => (
                    <Cell key={`capex-${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip key="capex-tooltip" formatter={(value) => formatCurrency(value as number, financialParams.currency)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-4">25 {t('projectDetails.years')} {t('projectDetails.cashFlow')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={cashFlowData}>
                <CartesianGrid key="cashflow-grid" strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis key="cashflow-xaxis" dataKey="year" stroke="#64748b" />
                <YAxis key="cashflow-yaxis" stroke="#64748b" />
                <Tooltip
                  key="cashflow-tooltip"
                  formatter={(value) => formatCurrency(value as number, financialParams.currency)}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                />
                <Line key="cashflow-line" type="monotone" dataKey="cumulative" stroke="#10b981" strokeWidth={2} name={t('projectDetails.cumulative')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 25-Year Financial Projection Table */}
        <div className="mt-8 bg-slate-50 rounded-lg border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
            <span>25-Year Financial Projection & NPV Calculation</span>
            <span className="text-xs font-normal text-slate-500 bg-green-100 px-2 py-1 rounded">Discounted Cash Flow Analysis</span>
          </h3>
          <p className="text-xs text-slate-600 mb-4">
            <strong>NPV Formula:</strong> NPV = Σ [Net Cash Flow / (1 + Discount Rate)^Year] - Initial Investment  •  
            <strong className="ml-2">Discount Rate:</strong> {financialParams.discountRate}%
          </p>
          
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr className="border-b-2 border-slate-300">
                  <th className="text-center py-2 px-3 font-bold text-slate-700">Year</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Production<br/>(kWh)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Degradation<br/>(%)</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Revenue<br/>({financialParams.currency})</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">OPEX<br/>({financialParams.currency})</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Net Cash Flow<br/>({financialParams.currency})</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700">Discount<br/>Factor</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700 bg-green-50">Discounted CF<br/>({financialParams.currency})</th>
                  <th className="text-right py-2 px-3 font-bold text-slate-700 bg-amber-50">Cumulative NPV<br/>({financialParams.currency})</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-200 bg-red-50">
                  <td className="py-2 px-3 text-center font-bold text-red-900">0</td>
                  <td className="text-right py-2 px-3 font-mono text-slate-600">-</td>
                  <td className="text-right py-2 px-3 font-mono text-slate-600">-</td>
                  <td className="text-right py-2 px-3 font-mono text-slate-600">-</td>
                  <td className="text-right py-2 px-3 font-mono text-slate-600">-</td>
                  <td className="text-right py-2 px-3 font-mono font-bold text-red-900">-{formatNumber(totalCapex, 0)}</td>
                  <td className="text-right py-2 px-3 font-mono text-slate-900">1.000</td>
                  <td className="text-right py-2 px-3 font-mono font-bold text-red-900 bg-red-50">-{formatNumber(totalCapex, 0)}</td>
                  <td className="text-right py-2 px-3 font-mono font-bold text-red-900 bg-red-50">-{formatNumber(totalCapex, 0)}</td>
                </tr>
                {financialMetrics.cashFlow.map((cf, index) => {
                  const year = cf.year;
                  const degradationFactor = Math.pow(1 - systemConfig.losses.degradation / 100, year);
                  const production = energyProduction.annualProduction * degradationFactor;
                  const degradationPercent = (1 - degradationFactor) * 100;
                  const revenue = cf.revenue;
                  const opex = Object.values(financialParams.opex).reduce((sum, val) => sum + val, 0);
                  const netCashFlow = cf.netCashFlow;
                  const discountFactor = 1 / Math.pow(1 + financialParams.discountRate / 100, year);
                  const discountedCF = netCashFlow * discountFactor;
                  const cumulativeNPV = cf.cumulativeCashFlow;
                  
                  const isPaybackYear = index > 0 && 
                    financialMetrics.cashFlow[index - 1].cumulativeCashFlow < 0 && 
                    cumulativeNPV >= 0;
                  
                  return (
                    <tr key={year} className={`border-b border-slate-100 hover:bg-slate-50 ${isPaybackYear ? 'bg-green-50' : ''}`}>
                      <td className={`py-2 px-3 text-center font-bold ${isPaybackYear ? 'text-green-900' : 'text-slate-700'}`}>
                        {year}
                        {isPaybackYear && <span className="ml-1 text-[10px] bg-green-500 text-white px-1 rounded">PAYBACK</span>}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">{formatNumber(production, 0)}</td>
                      <td className="text-right py-2 px-3 font-mono text-red-600">{degradationPercent.toFixed(1)}%</td>
                      <td className="text-right py-2 px-3 font-mono text-green-700">{formatNumber(revenue, 0)}</td>
                      <td className="text-right py-2 px-3 font-mono text-red-600">{formatNumber(opex, 0)}</td>
                      <td className="text-right py-2 px-3 font-mono font-bold text-slate-900">{formatNumber(netCashFlow, 0)}</td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">{discountFactor.toFixed(3)}</td>
                      <td className="text-right py-2 px-3 font-mono font-bold text-green-700 bg-green-50">{formatNumber(discountedCF, 0)}</td>
                      <td className={`text-right py-2 px-3 font-mono font-bold ${cumulativeNPV >= 0 ? 'text-green-900' : 'text-red-900'} bg-amber-50`}>
                        {formatNumber(cumulativeNPV, 0)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                  <td className="py-3 px-3 text-center text-slate-900">TOTAL</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">-</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">-</td>
                  <td className="text-right py-3 px-3 font-mono text-green-700">{formatNumber(financialMetrics.totalRevenue, 0)}</td>
                  <td className="text-right py-3 px-3 font-mono text-red-600">
                    {formatNumber(Object.values(financialParams.opex).reduce((sum, val) => sum + val, 0) * 25, 0)}
                  </td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">
                    {formatNumber(financialMetrics.totalRevenue - (Object.values(financialParams.opex).reduce((sum, val) => sum + val, 0) * 25), 0)}
                  </td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900">-</td>
                  <td className="text-right py-3 px-3 font-mono text-slate-900 bg-green-50">-</td>
                  <td className="text-right py-3 px-3 font-mono font-bold text-lg bg-amber-100 text-amber-900">
                    {formatNumber(financialMetrics.npv, 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Financial Parameters:</p>
              <p className="text-slate-600">• Electricity Rate: {formatCurrency(financialParams.electricityRate, financialParams.currency)}/kWh</p>
              <p className="text-slate-600">• Discount Rate: {financialParams.discountRate}%</p>
              <p className="text-slate-600">• Project Lifetime: {financialParams.projectLifetime} years</p>
              <p className="text-slate-600">• Degradation: {systemConfig.losses.degradation}%/year</p>
            </div>
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Investment Summary:</p>
              <p className="text-slate-600">• Total CAPEX: {formatCurrency(totalCapex, financialParams.currency)}</p>
              <p className="text-slate-600">• Annual OPEX: {formatCurrency(Object.values(financialParams.opex).reduce((sum, val) => sum + val, 0), financialParams.currency)}</p>
              <p className="text-slate-600">• Total Revenue: {formatCurrency(financialMetrics.totalRevenue, financialParams.currency)}</p>
              <p className="text-green-700 font-semibold">• Net Profit: {formatCurrency(financialMetrics.netProfit, financialParams.currency)}</p>
            </div>
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Key Metrics:</p>
              <p className="text-amber-700 font-bold">• NPV: {formatCurrency(financialMetrics.npv, financialParams.currency)}</p>
              <p className="text-slate-600">• IRR: {formatPercent(financialMetrics.irr)}</p>
              <p className="text-slate-600">• ROI: {formatPercent(financialMetrics.roi)}</p>
              <p className="text-slate-600">• Payback: {financialMetrics.paybackPeriod.toFixed(1)} years</p>
            </div>
            <div className="bg-white rounded p-3 border border-slate-200">
              <p className="font-semibold text-slate-700 mb-1">Calculation Method:</p>
              <p className="text-slate-600 text-[11px] leading-tight">
                ✓ Discounted Cash Flow (DCF) analysis<br/>
                ✓ Year-by-year degradation modelling<br/>
                ✓ Time value of money adjustment<br/>
                ✓ IEC 61724 compliant methodology
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Environmental Impact */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{t('projectDetails.environmentalImpact')}</h2>
            <p className="text-sm text-slate-600">{financialParams.projectLifetime} years positive environmental contribution</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-600 mb-1">{t('projectDetails.co2EmissionsAvoided')}</p>
            <p className="text-3xl font-bold text-amber-600">
              {formatNumber(environmentalImpact.co2Avoided, 0)} {t('projectDetails.tons')}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-600 mb-1">{t('projectDetails.equivalentTreesPlanted')}</p>
            <p className="text-3xl font-bold text-amber-600">
              {environmentalImpact.treesEquivalent.toLocaleString()}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-600 mb-1">{t('projectDetails.coalAvoided')}</p>
            <p className="text-3xl font-bold text-amber-600">
              {formatNumber(environmentalImpact.coalAvoided, 0)} {t('projectDetails.tons')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// MetricCard Component
interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: 'success' | 'warning' | 'info' | 'neutral';
}

function MetricCard({ title, value, subtitle, icon: Icon, variant }: MetricCardProps) {
  const colors = {
    success: 'from-green-50 to-emerald-50 border-green-200',
    warning: 'from-amber-50 to-orange-50 border-amber-200',
    info: 'from-blue-50 to-cyan-50 border-blue-200',
    neutral: 'from-gray-50 to-gray-100 border-gray-200',
  };

  const iconColors = {
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
    neutral: 'bg-gray-500',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[variant]} rounded-xl border-2 shadow-sm p-6`}>
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm font-medium text-slate-600">{title}</p>
        <div className={`${iconColors[variant]} p-2 rounded-lg`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value}</p>
      <p className="text-sm text-slate-600">{subtitle}</p>
    </div>
  );
}